import {AfterViewInit, booleanAttribute, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostBinding, input, Input, OnDestroy, OnInit, QueryList, Self, signal, ViewChildren} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {AbstractControl, ControlValueAccessor, FormArray, FormControl, NgControl, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {from, fromEvent, mergeMap, Subject, tap} from "rxjs";
import {map} from "rxjs/operators";
import {SpesialtegnType} from "felles-ng2/støttefunksjoner/tastaturhendelser";
import {erTomEllerNil, trimTilTomStreng} from "felles-ng2/støttefunksjoner/strenger";
import {MatFormFieldControl} from "@angular/material/form-field";
import {Mobilnummer} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {BooleanInput, coerceBooleanProperty} from "@angular/cdk/coercion";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {første} from "felles-ng2/støttefunksjoner/lister";
import {TranslateModule} from "@ngx-translate/core";

const debug = initDebugger('OtpSkjemaKomponent');

/**
 * Skjema for angivelse av OTP.
 *
 * Fungerer med Ng template-basert form ([(ngModel)]="...") og Ng reactive form ([formControl]="...").
 * Fungerer med <mat-form-field> fra Material.
 */
@Component({
    selector: 'otp-felt',
    templateUrl: 'otp-felt.html',
    styleUrl: 'otp-felt.scss',
    host: { 'data-testid': 'otp-felt' },
    providers: [{ provide: MatFormFieldControl, useExisting: OtpSkjemaKomponent }],
    imports: [
        ReactiveFormsModule,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OtpSkjemaKomponent implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<string> {

    @ViewChildren('inputfelt') inputfelter!: QueryList<ElementRef<HTMLInputElement>>;
    skjema = new FormArray<FormControl<number | null>>([]);

    /** Dersom sann vises melding om ugyldig engangskode under tekstfeltene */
    ugyldig = input(false);

    protected otp = signal<string | null>(null);

    constructor(/** skjemakontrollen tilknyttet denne komponenten via formControl eller ngModel direktiver*/
                @Self() private tilknyttetSkjemakontroll: NgControl,
                private destroyRef: DestroyRef) {
        for (let i = 0; i < 6; i++)
            this.skjema.push(new FormControl<number | null>(null, {nonNullable: false, validators: [Validators.required, Validators.min(0), Validators.max(9)]}));

        // setter valueAccessor direkte fremfor via DI og providers i @Component med `{provide: NG_VALUE_ACCESSOR, multi:true, useExisting: MobilnummerFeltKomponent}`
        // for å unngå sirkulær avhengighet.
        this.tilknyttetSkjemakontroll.valueAccessor = this;

        this.lyttTilSkjemaEndringer();
    }

    vedPaste(hendelse: ClipboardEvent, indeks: number) {
        debug('vedPaste', hendelse, indeks)
        hendelse.stopPropagation();
        hendelse.preventDefault();
        let clipboardData = hendelse.clipboardData!;
        return this.skrivTilSkjema(clipboardData.getData('text'));
    }

    //ng2 livssykelmetoder

    ngOnInit(): void {
        //sørger for at valideringsfeil knyttet til det lokale skjemaet propagerer til skjemakontroll tilknyttet denne komponenten
        //kan ikke gjøres i konstruktør da tilknyttetSkjemakontroll.control ikke er satt enda
        this.tilknyttetSkjemakontroll.control!.addValidators(this.tilknyttetSkjemakontrollValidator())
    }

    ngAfterViewInit(): void {
        //nå er inputfelter initialisert
        this.håndterNavigasjonMellomFelter();
    }

    ngOnDestroy() {
        this.stateChanges.complete();
    }

    //private hjelpemetoder

    private hentInputfelter(): HTMLInputElement[] {
        return Array.from(this.inputfelter).map(ref => ref.nativeElement);
    }

    private håndterNavigasjonMellomFelter() {
        let felter = this.hentInputfelter();
        const felterSomObservable = from(felter).pipe(takeUntilDestroyed(this.destroyRef));

        felterSomObservable
          .pipe(mergeMap(element => fromEvent(element, 'focus')))
          .subscribe(() => {
              debug('focused=true')
              this.focused = true;
          });
        felterSomObservable
          .pipe(mergeMap(element => fromEvent(element, 'blur')))
          .subscribe(() => {
              debug('focused=false')
              this.focused = false;
          });

        felterSomObservable
          .pipe(
            mergeMap((element, indeks) =>
              fromEvent(element, 'keyup').pipe(map(hendelse => ({hendelse: hendelse as TastaturHendelsePåInputfelt, indeks})))),)
          .subscribe(({hendelse, indeks}) => {
              debug(`keyup: ${hendelse.key}`);
              //dersom angitt verdi er et tall, sett verdi og gå til neste felt
              if (!isNaN(parseInt(hendelse.key))) {
                  this.skjema.at(indeks).setValue(parseInt(hendelse.key)); //uten dette vil ikke det å taste et nytt tall i et felt som allerede har en verdi ha noen effekt
                  return this.flyttFokusTilFelt(indeks + 1, felter);
              }

              switch (hendelse.key as SpesialtegnType) {
                //navigasjon mellom felter
                  case 'ArrowLeft' :
                      return this.flyttFokusTilFelt(indeks - 1, felter);
                  case 'ArrowRight' :
                      return this.flyttFokusTilFelt(indeks + 1, felter);
                  case 'Backspace' :
                  case 'Delete' :
                      this.skjema.at(indeks).setValue(null);
                      return this.flyttFokusTilFelt(indeks - 1, felter);

                //safari vil tilby bruker å kopiere inn engangskode fra sms - dette avsluttes med et "enter"
                //som vi her lytter til for å håndtere innlimt verdi
                //merk at dette også vil føre til submit da vi ikke stopper boblingen av enter-eventet
                  case 'Enter':
                      return this.skrivTilSkjema(hendelse.target.value);
              }
          });
    }

    private skrivTilSkjema(otp: any) {
        debug('skrivSifre', otp)
        if (erTomEllerNil(otp)) return;
        const somStreng = trimTilTomStreng(otp);
        if (!/\d{6}/.test(somStreng)) return;

        Array.from(somStreng)
          .map(verdi => parseInt(verdi))
          .filter((tallverdi, indeks) => indeks < this.skjema.controls.length)
          .forEach((tallverdi, indeks) => {
              debug(`skrivSifre, setter felt ${indeks} til '${tallverdi}'`)
              this.skjema.controls.at(indeks)!.setValue(tallverdi)
          });

        //gi fokus til siste felt (dersom inputfelter har rukket å bli init)
        if (this.inputfelter) this.flyttFokusTilFelt(5, this.hentInputfelter())
    }

    /** Gi angitt felt fokus, men sørg for at indeks ikke er utenfor samlingens størrelse. */
    private flyttFokusTilFelt(indeks: number, felter: HTMLInputElement[]) {
        felter[Math.max(Math.min(indeks, felter.length - 1), 0)].focus();
    }

    private lyttTilSkjemaEndringer() {
        this.skjema.valueChanges
          .pipe(
            takeUntilDestroyed(),
            tap(verdier => this.propagerValideringsfeilTilTilknyttetSkjemakontroll()),
            map(verdier => verdier.join(''))
          )
          .subscribe(otpVerdi => {
              debug(`skjema endret, otpVerdi:${otpVerdi}, skjema.valid: ${this.skjema.valid}`)
              //eksponerer null dersom skjema er ugyldig, ellers mobilnummer med forvalg
              this.otp.set(this.skjema.valid ? otpVerdi : null);
              debug("eksponerer verdi", this.otp(), this.skjema.valid)
              if (erIkkeNil(this.vedEndring)) this.vedEndring(this.otp());
          })
    }

    /** Sørger for at tilknyttet skjemakontroll kjører validering, som i sin tur vil kalle {@link #tilknyttetSkjemakontrollValidator} */
    private propagerValideringsfeilTilTilknyttetSkjemakontroll() {
        this.tilknyttetSkjemakontroll.control?.updateValueAndValidity({onlySelf: true, emitEvent: false})
    }

    /** Returnerer valideringsfeil registrert på {@link #skjema}.
     * Se også {@link #propagerValideringsfeilTilTilknyttetSkjemakontroll} */
    private tilknyttetSkjemakontrollValidator(): ValidatorFn {
        return (control: AbstractControl<Mobilnummer>): ValidationErrors | null => {
            //les valideringsfeil fra feltKontroll og returner disse
            let førsteFeil = første(this.skjema.controls
              .filter(kontroller => erIkkeNil(kontroller.errors))
              .map(kontroller => kontroller.errors));
            debug("tilknyttetSkjemakontrollValidator", this.skjema.valid, førsteFeil)
            if (this.skjema.value.every(v => erNil(v))) {
                debug("tilknyttetSkjemakontrollValidator alle null")
                return null;
            }
            return førsteFeil;
        }
    }

    //ControlValueAccessor implementasjon

    /** Settes via {@link #registerOnChange} (ifbm ControlValueAccessor)*/
    private vedEndring: Function | null = null;

    /** Settes via {@link #registerOnTouched} (ifbm ControlValueAccessor)*/
    private onTouched: Function | null = null;

    registerOnChange(vedEndring: Function): void {
        this.vedEndring = vedEndring;
    }

    registerOnTouched(vedBerørt: Function): void {
        this.onTouched = vedBerørt;
    }

    setDisabledState(isDisabled: boolean): void {
        debug("setDisabledState", isDisabled)
        if (isDisabled) this.skjema.disable();
        else this.skjema.enable();
    }

    writeValue(otp: string | null): void {
        debug("writeValue", otp)
        this.skrivTilSkjema(otp)
        this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
    }

    //MatFormFieldControl implementasjon

    /** brukes til å lage unike IDer, se {@link #id}*/
    static nesteId = 0;

    private _placeholder: string = '';
    private _required = false;

    @Input('aria-describedby') userAriaDescribedBy!: string; //@todo hva gjør vi med denne?

    get placeholder() {
        return this.shouldLabelFloat ? this._placeholder : '';
    }

    @Input() set placeholder(plh) {
        this._placeholder = plh;
        this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
    }

    get required() {
        return this._required ||
          (this.tilknyttetSkjemakontroll.control?.hasValidator(Validators.required) ?? false);
    }

    @Input({transform: booleanAttribute}) set required(req: boolean) {
        this._required = req;
        if (req) this.skjema.addValidators(Validators.required);
        else this.skjema.removeValidators(Validators.required);
        this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
    }

    get disabled(): boolean {
        return this.skjema.disabled
    }

    @Input() set disabled(value: BooleanInput) {
        this.setDisabledState(coerceBooleanProperty(value))
        this.stateChanges.next(); //signaliser til mat-form-field at verdi kan ha blitt endret
    }

    public get ngControl(): NgControl {
        return this.tilknyttetSkjemakontroll;
    }

    readonly stateChanges = new Subject<void>();

    @HostBinding() id = `otp-felt-${OtpSkjemaKomponent.nesteId++}`;

    controlType = 'otp-felt';

    /** se {@link #vedFokusMottatt} / {@link #vedFokusMistet} */
    focused = false;

    get value(): Mobilnummer | null {
        return this.otp();
    }

    set value(klokkeslett: Mobilnummer | null) {
        this.writeValue(klokkeslett);
    }

    get empty() {
        return this.skjema.value.every(feltVerdi => erIkkeNil(feltVerdi));
    }

    get shouldLabelFloat() {
        return this.focused || !this.empty;
    }

    get errorState(): boolean {
        return this.skjema.invalid;
    }

    setDescribedByIds(ids: string[]) {
        if (erNil(this.inputfelter)) return;
        this.hentInputfelter().forEach(felt => {
            felt.setAttribute('aria-describedby', ids.join(' '));
        });
    }

    onContainerClick(event: MouseEvent) {
        if ((event.target as Element).tagName.toLowerCase() == 'input') return; //trenger ikke å sette fokus dersom klikket var i inputfeltet
        if (erNil(this.inputfelter)) return;

        this.flyttFokusTilFelt(0, this.hentInputfelter());
    }
}

type TastaturHendelsePåInputfelt = KeyboardEvent & { target: HTMLInputElement };
